import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { Search } from 'lucide-react';
import { Input } from '@atoms/Input';
import { CategoryIcon, categoryIconMeta } from '@/utils/categoryIcons';
import {
  CATEGORY_ICON_GROUPS,
  type CategoryIconDefinition,
} from '@/utils/categoryIconRegistry';

interface CategoryIconPickerProps {
  value: string;
  onChange: (key: string) => void;
}

type FlatIcon = CategoryIconDefinition & { groupId: string; groupLabel: string };

export function CategoryIconPicker({ value, onChange }: CategoryIconPickerProps) {
  const [query, setQuery] = useState('');
  const [activeGroup, setActiveGroup] = useState('all');

  const allIcons = useMemo<FlatIcon[]>(
    () =>
      CATEGORY_ICON_GROUPS.flatMap((group) =>
        group.icons.map((icon) => ({
          ...icon,
          groupId: group.id,
          groupLabel: group.label,
        })),
      ),
    [],
  );

  const filteredIcons = useMemo(() => {
    const q = query.trim().toLocaleLowerCase('tr');
    return allIcons.filter((icon) => {
      if (activeGroup !== 'all' && icon.groupId !== activeGroup) return false;
      if (!q) return true;
      return (
        icon.label.toLocaleLowerCase('tr').includes(q) ||
        icon.key.includes(q) ||
        icon.groupLabel.toLocaleLowerCase('tr').includes(q)
      );
    });
  }, [activeGroup, allIcons, query]);

  const selectedMeta = categoryIconMeta(value);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-slate-50 px-3 py-2.5">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm">
          <CategoryIcon name="" icon={value} size={24} />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-900">{selectedMeta.label}</p>
          <p className="truncate text-xs text-slate-500">{value}</p>
        </div>
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="İkon ara (ör. et, temizlik, elektronik)..."
          className="pl-9"
        />
      </div>

      <div className="flex flex-wrap gap-1.5">
        <GroupPill
          active={activeGroup === 'all'}
          label="Tümü"
          onClick={() => setActiveGroup('all')}
        />
        {CATEGORY_ICON_GROUPS.map((group) => (
          <GroupPill
            key={group.id}
            active={activeGroup === group.id}
            label={group.label}
            onClick={() => setActiveGroup(group.id)}
          />
        ))}
      </div>

      <div className="rounded-lg border border-gray-200 bg-white">
        {filteredIcons.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-slate-500">
            Aramanızla eşleşen ikon bulunamadı.
          </p>
        ) : (
          <div className="grid max-h-64 grid-cols-6 gap-1 overflow-y-auto p-2 sm:grid-cols-8 md:grid-cols-10">
            {filteredIcons.map((icon) => (
              <button
                key={icon.key}
                type="button"
                title={icon.label}
                aria-label={icon.label}
                aria-pressed={value === icon.key}
                onClick={() => onChange(icon.key)}
                className={clsx(
                  'flex aspect-square items-center justify-center rounded-lg border transition-colors',
                  value === icon.key
                    ? 'border-brand-500 bg-brand-50 ring-2 ring-brand-500/20'
                    : 'border-transparent hover:border-gray-200 hover:bg-slate-50',
                )}
              >
                <CategoryIcon name="" icon={icon.key} size={22} />
              </button>
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-slate-500">
        {filteredIcons.length} ikon · Perakende ve toptan platformlarındaki yaygın kategori grupları
      </p>
    </div>
  );
}

function GroupPill({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
        active
          ? 'bg-brand-500 text-white'
          : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
      )}
    >
      {label}
    </button>
  );
}
